import React, { useCallback, useEffect } from "react";
import { Tabs } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectYear as selectCurrentYear,
  selectMonth as selectCurrentMonth,
  selectDay as selectCurrentDay,
  selectCurrentCO2,
  selectShipType,
  selectCurrentLayerGroup,
  selectCategory,
  selectPanelsByLayerGroup,
} from "@/selector";
import { actionCreators } from "@/duck/panel";
import { actionCreators as timelineActionCreators } from "@/duck/timeline";
import {
  switchLayers,
  getDateRanges,
  getDefaultYear,
  getDefaultMonth,
  getDefaultDay,
} from "@/util";
import ForecastDateFilter from "@/components/dashboard/filter/ForecastDateFilter";
import HistoryDateFilter from "@/components/dashboard/filter/HistoryDateFilter";
import HistoryRanges from "@/util/history_range";
import ForecastRanges from "@/util/forecast_range";
import IrregularRanges from "@/util/irregular_range";
import "@/components/dashboard/filter/DateFilter.less";

const DateFilter = () => {
  const { t } = useTranslation();
  const panels = useSelector(selectPanelsByLayerGroup);
  const currentLayerGroup = useSelector(selectCurrentLayerGroup);
  const category = useSelector(selectCategory);
  const dateRanges = getDateRanges(category);
  const currentCO2 = useSelector(selectCurrentCO2);
  const shipType = useSelector(selectShipType);
  const year = useSelector(selectCurrentYear) || getDefaultYear(category);
  const month = useSelector(selectCurrentMonth) || getDefaultMonth(category);
  const day = useSelector(selectCurrentDay) || getDefaultDay(category);
  const dispatch = useDispatch();
  let ranges = HistoryRanges?.[year]?.[month] || [];
  if (category === "irregular") {
    ranges = IrregularRanges?.[year]?.[month] || [];
  } else if (category === "forecast") {
    ranges = ForecastRanges?.[year]?.[month] || [];
  }
  console.log(category, ranges, year, month);

  useEffect(() => {
    const layers = switchLayers(
      currentLayerGroup,
      category,
      year,
      month,
      day,
      panels,
      shipType,
      ranges
    );
    if (layers) {
      dispatch(
        actionCreators.panel.layer.update({
          currentLayerGroup,
          layers,
        })
      );
    }
  }, [
    dispatch,
    currentLayerGroup,
    category,
    year,
    month,
    day,
    panels,
    currentCO2,
    shipType,
    ranges,
  ]);

  const switchTab = useCallback(
    (activeKey) => {
      dispatch(
        timelineActionCreators.timeline.category({ category: activeKey })
      );
    },
    [dispatch]
  );

  const selectShip = useCallback(
    (shipType) => {
      dispatch(timelineActionCreators.timeline.shiptype({ shipType }));
      const layers = switchLayers(
        currentLayerGroup,
        category,
        year,
        month,
        day,
        panels,
        shipType
      );
      if (layers) {
        dispatch(
          actionCreators.panel.layer.update({ currentLayerGroup, layers })
        );
      }
    },
    [dispatch, year, month, day, category, currentLayerGroup, panels]
  );

  const selectYear = useCallback(
    (y) => {
      dispatch(timelineActionCreators.timeline.year({ year: y }));
      const layers = switchLayers(
        currentLayerGroup,
        category,
        y,
        month,
        day,
        panels,
        shipType,
        ranges
      );
      if (layers) {
        dispatch(
          actionCreators.panel.layer.update({ currentLayerGroup, layers })
        );
      }
    },
    [
      dispatch,
      month,
      day,
      category,
      currentLayerGroup,
      panels,
      shipType,
      ranges,
    ]
  );

  const selectMonth = useCallback(
    (m) => {
      dispatch(timelineActionCreators.timeline.month({ month: m }));
      const layers = switchLayers(
        currentLayerGroup,
        category,
        year,
        m,
        day,
        panels,
        shipType,
        ranges
      );
      if (layers) {
        dispatch(
          actionCreators.panel.layer.update({ currentLayerGroup, layers })
        );
      }
    },
    [dispatch, year, day, category, currentLayerGroup, panels, shipType, ranges]
  );

  const selectDay = useCallback(
    (d) => {
      dispatch(timelineActionCreators.timeline.day({ day: d }));
      const layers = switchLayers(
        currentLayerGroup,
        category,
        year,
        month,
        d,
        panels,
        shipType,
        ranges
      );
      if (layers) {
        dispatch(
          actionCreators.panel.layer.update({ currentLayerGroup, layers })
        );
      }
    },
    [
      dispatch,
      year,
      month,
      category,
      currentLayerGroup,
      panels,
      shipType,
      ranges,
    ]
  );

  return (
    <div className="date-selector">
      <Tabs
        defaultActiveKey={category}
        onChange={(activeKey) => switchTab(activeKey)}
      >
        <Tabs.TabPane tab={t("timeline.history")} key="history">
          {category === "history" && (
            <HistoryDateFilter
              year={year}
              month={month}
              day={day}
              shipType={shipType}
              dateRanges={dateRanges}
              selectYear={selectYear}
              selectMonth={selectMonth}
              selectDay={selectDay}
              selectShip={selectShip}
              layerGroup={currentLayerGroup}
              ranges={ranges}
            />
          )}
        </Tabs.TabPane>

        <Tabs.TabPane tab={t("timeline.prediction")} key="forecast">
          {category === "forecast" && (
            <ForecastDateFilter
              year={year}
              month={month}
              day={day}
              shipType={shipType}
              dateRanges={dateRanges}
              selectYear={selectYear}
              selectMonth={selectMonth}
              selectDay={selectDay}
              selectShip={selectShip}
              layerGroup={currentLayerGroup}
              ranges={ranges}
            />
          )}
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default DateFilter;
